import React, {
  useMemo,
  useContext,
  FC,
}                                from 'react';
import {
  Formik,
  Field,
  FormikConfig,
}                                from 'formik';
import styled                    from 'styled-components';
import {
  IFeeValues,
  TMappedPricing,
}                                from '@typings/accountManagement';
import { TDataTableRow }         from '@typings/components';
import {
  IBulkPricingList,
  IPricing,
}                                from '@typings/invoiceManagement';
import { EFullFeeType, ENotFullFeeType }          from '@enums/invoiceManagement';
import { formatCurrency }        from '@utils/commonFunctions';
import { FormikRefContext }      from '@utils/FormikRefContext';
import { useToggle }             from '@utils/useToggle';
import { defaultPricingSchema }  from '@modules/invoiceManagement/utils/validation';
import { PRICING_LABELS }        from '@modules/invoiceManagement/utils/suggestions';
import { DefaultPricingForm }    from '@modules/invoiceManagement/components/DefaultPricingForm';
import { CurrencyInput, Switch } from '@components/index';
import { ICheckboxOption }       from '@components/Checkbox/CheckboxFieldGroup';

const Wrapper = styled.div`
  && {
    td {
      font-size: 15px;

      div {
        width: 125px;
      }
    }
  }
`;

const showChangeableField = (key: any, maxLength: number) => (
  <>
    <Field
      id        = {key}
      name      = {`${ENotFullFeeType[key]}.value`}
      component = {CurrencyInput}
      maxLength = {maxLength}
    />
  </>
);

const getTableRows = (
  isEditing      : boolean,
  defaultPricing : TMappedPricing,
): TDataTableRow[] => Object.keys(defaultPricing).map((key: keyof IPricing) => [
  { value: PRICING_LABELS[key] },
  {
    align : 'center',
    value : isEditing
      ? showChangeableField(key, 8)
      : formatCurrency(defaultPricing[key].value ?? 0),
  },
  {
    align : 'right',
    value : (
      <Field
        id        = {`${EFullFeeType[key]}.taxable`}
        name      = {`${EFullFeeType[key]}.taxable`}
        disabled  = {!isEditing}
        component = {Switch}
      />
    ),
  },
]);

interface IProps {
  defaultPricing       : IFeeValues[];
  isEditing            : boolean;
  isLoading            : boolean;
  organizationsOptions : ICheckboxOption[];
  onSaveChanges        : FormikConfig<IFeeValues[]>['onSubmit'];
  onResetForm          : FormikConfig<IFeeValues[]>['onReset'];
  onUpdateBulkFees     : (feesValues: IFeeValues[], accountList: IBulkPricingList) => void;
  toggleIsEditing      : () => void;
}

export const DefaultPricingPage: FC<IProps> = ({
  isEditing,
  isLoading,
  defaultPricing,
  organizationsOptions,
  onSaveChanges,
  onResetForm,
  onUpdateBulkFees,
  toggleIsEditing,
}) => {
  const formikRef = useContext(FormikRefContext);

  const [isBulkAccountsModal, toggleIsBulkAccountsModal] = useToggle();

  const mappedDefaultPricing: TMappedPricing = defaultPricing.reduce((acc, price) => {
    const { type, value, taxable, userType } = price;

    return { ...acc,
      [EFullFeeType[userType === EFullFeeType.personalFee ? EFullFeeType.personalFee : type - 1]]: {
        value,
        taxable,
      } };
  }, {});

  const tableRows = useMemo<TDataTableRow[]>(
    () => getTableRows(isEditing, mappedDefaultPricing),
    [mappedDefaultPricing, isEditing],
  );

  return (
    <Wrapper>
      <Formik
        innerRef         = {formikRef}
        onSubmit         = {onSaveChanges}
        onReset          = {onResetForm}
        initialValues    = {defaultPricing}
        validationSchema = {defaultPricingSchema}
      >
        <DefaultPricingForm
          rows                    = {tableRows}
          isLoading               = {isLoading}
          isEditing               = {isEditing}
          isBulkAccounts          = {isBulkAccountsModal}
          organizationsOptions    = {organizationsOptions}
          handleBulkAccountsModal = {toggleIsBulkAccountsModal}
          handleUpdateBulkFees    = {onUpdateBulkFees}
          toggleIsEditing         = {toggleIsEditing}
          toggleIsBulkAccounts    = {toggleIsBulkAccountsModal}
        />
      </Formik>
    </Wrapper>
  );
};
